import lodash from 'lodash';
import { getStudy } from './study';
import { jsonHelper, QueryNodeRegistry } from '@complion/query';
import { mutateGraphQL, queryGraphQL } from 'common/util/query';
import { loadDataSourceItems } from 'common/actions/dataSources';
import { userDefaultInstitutionIdSelector } from 'common/selectors';
import { getPredicateGroups } from 'binder/util/PredicateGroupingVisitor';
import {
    addApplicationMessage,
    addApplicationMessages
} from 'common/actions/applicationMessagesActions';
import { doaFeatureAccess } from 'common/features/predicates';
import { delegationsList } from 'pages/delegationOfAuthority/selectors';
import { delegationsUnderPITenurePredicate } from 'pages/delegationOfAuthority/v2/util/predicates';
import { node, nodeWithArgs, toMutation, toQuery } from 'common/util/queryHelper';
import {
    getBinderTemplateRequirements,
    getDataSourceIdsFromBinderTemplate
} from '../../study/actions/createStudy';
import { loadBinderUsers } from 'binder/actions/';
import { createDelegationsV2 } from 'pages/study/actions/createStudy';
import { browserHistory } from 'react-router';
import { STUDY_TEAM_CAPITAL, STUDY_USER_TYPE } from 'components/common/Study/StudyTeamMemberForm';
import { loadStudySignaturesV2 } from '../../delegationOfAuthority/v2/actions/studySignaturesV2';

import moment from 'moment';
import { convertToISOString } from 'common/util/dateTime';
import { getActivePI } from '../../delegationOfAuthority/selectors/index.js';
import { updateStudy } from 'pages/studyOverview/actions/study';

export const ADD_EXTERNAL_USER = 'ADD_EXTERNAL_USER';
export const ADD_STUDY_USER = 'ADD_STUDY_USER';
export const CLEAR_EXTERNAL_USER = 'CLEAR_EXTERNAL_USER';
export const CLEAR_EXTERNAL_USER_FOR_STUDY = 'CLEAR_EXTERNAL_USER_FOR_STUDY';
export const LOAD_STUDY_DESCRIPTORS = 'LOAD_STUDY_DESCRIPTORS';
export const LOAD_BINDER_DESCRIPTORS = 'LOAD_BINDER_DESCRIPTORS';
export const LOAD_EXTERNAL_USER_FOR_STUDY = 'LOAD_EXTERNAL_USER_FOR_STUDY';
export const LOAD_EXTERNAL_USERS_FOR_STUDY = 'LOAD_EXTERNAL_USERS_FOR_STUDY';
export const LOAD_EXTERNAL_USERS_FOR_STUDY_WITH_PAGINATION = 'LOAD_EXTERNAL_USERS_FOR_STUDY_WITH_PAGINATION';
export const LOAD_STUDY_ROLES_BY_INSTITUTION = 'LOAD_STUDY_ROLES_BY_INSTITUTION';
export const LOAD_STUDY_STAGES = 'LOAD_STUDY_STAGES';
export const LOAD_STUDY_TEAM = 'LOAD_STUDY_TEAM';
export const LOAD_ALL_STUDY_TEAM = 'LOAD_ALL_STUDY_TEAM';
export const LOAD_INSTITUTION_STUDY_TEAM_MEMBERS = 'LOAD_INSTITUTION_STUDY_TEAM_MEMBERS';
export const MATCH_STUDY_TEAM_TO_NAME_OF_RESEARCHER = 'MATCH_STUDY_TEAM_TO_NAME_OF_RESEARCHER';
export const UNLOAD_STUDY_SETTINGS_TEAM = 'UNLOAD_STUDY_SETTINGS_TEAM';
export const PROCESSING_STUDY_SETTINGS = 'PROCESSING_STUDY_SETTINGS';
export const CREATE_STUDY_MONITOR = 'CREATE_STUDY_MONITOR';
export const WAITING_FOR_LOAD_EXTERNAL_USER_FOR_STUDY = 'WAITING_FOR_LOAD_EXTERNAL_USER_FOR_STUDY';
export const WAITING_FOR_LOAD_EXTERNAL_USERS_FOR_STUDY = 'WAITING_FOR_LOAD_EXTERNAL_USERS_FOR_STUDY';
export const SET_STUDY_NOTIFIED_USERS_LOADED_STATUS = 'SET_STUDY_NOTIFIED_USERS_LOADED_STATUS';
export const LOAD_STUDY_NOTIFIED_USERS_SUCCESS = 'LOAD_STUDY_NOTIFIED_USERS_SUCCESS';
export const SAVE_STUDY_NOTIFIED_USERS_SUCCESS = 'SAVE_STUDY_NOTIFIED_USERS_SUCCESS';
export const CLEAR_STUDY_NOTIFIED_USERS = 'CLEAR_STUDY_NOTIFIED_USERS';
export const ADDED_STUDY_DELEGATIONS = 'ADDED_STUDY_DELEGATIONS';
export const SET_IS_EMPTY_DETAIL = 'SET_IS_EMPTY_DETAIL';
const PRINCIPAL_INVESTIGATOR_ROLE = 'Principal Investigator';

const parseDateFormat = (date) => {
    if (lodash.isNil(date)) {
        return null;
    }

    return moment.utc(date);
};

function validateResolverResponse(response, dispatch, errorMessage) {
    if (response && response.errors) {
        return dispatch(addApplicationMessage('error', errorMessage));
    }
}

function validateResponse(response, dispatch, errorMessage) {
    if (response && response.errors) {
        const errors = lodash.get(response, 'errors');
        const message = lodash.get(errors[0], 'message', '');
        const [statusCode] = message.split(' - ');
        if (statusCode === '404') {
            dispatch(addApplicationMessage('error', 'This Study Team Member has already been removed from the Study.'));
            window.location.reload();
        }
        else {
            dispatch(addApplicationMessage('error', errorMessage));
        }
    }
}

function nonAdminTeamMembers(studyCreatorId, teamMembers) {
    return lodash.filter(teamMembers, teamMember => teamMember.userId !== studyCreatorId);
}

export function getStudyRoles(institutionId) {
    return async (dispatch) => {
        const query = nodeWithArgs('rolesManagementForInstitution', {
            institutionId,
        }, [
            'id',
            'name',
            'isSystem',
            'active',
        ]);
        const studyRolesResponse = await queryGraphQL(toQuery(query));

        validateResolverResponse(studyRolesResponse, dispatch, 'Failed to fetch study essentials');

        const studyRoles = studyRolesResponse.data.rolesManagementForInstitution;
        const nonSystemRoles = lodash
            .filter(studyRoles, role => role.isSystem === false)
            .sort((role1, role2) => lodash.get(role1, 'name', '').localeCompare(lodash.get(role2, 'name', '')));
        const systemRoles = lodash
            .filter(studyRoles, role => role.isSystem === true)
            .sort((role1, role2) => lodash.get(role1, 'name', '').localeCompare(lodash.get(role2, 'name', '')));
        const newStudyRoles = lodash.concat(systemRoles, nonSystemRoles);

        dispatch({
            type: LOAD_STUDY_ROLES_BY_INSTITUTION,
            payload: newStudyRoles,
        });
    };
}

export function getBinderDescriptors() {
    return async (dispatch, getState) => {
        const state = getState();
        const { binder: { binderTemplateId } } = state;
        const institutionId = lodash.get(state, 'me.institution.id');
        const binderDescriptorsResponse = await getBinderTemplateRequirements(institutionId, binderTemplateId);
        if (binderDescriptorsResponse && binderDescriptorsResponse.errors || binderTemplate === null) {
            return dispatch(addApplicationMessage('error', 'Failed to load all study details'));
        }
        const binderTemplate = binderDescriptorsResponse.data.me.binderTemplate;
        const descriptors = binderDescriptorsResponse.data.descriptors;
        const deserializedObj = jsonHelper.parse(binderTemplate.rootQuery);
        const rootQuery = await QueryNodeRegistry.reconstituteDeserializedJson(deserializedObj);
        const predicateGroups = await getPredicateGroups(rootQuery);

        const newBinderTemplate = {
            ...binderTemplate,
            'rootQuery': rootQuery,
            'predicateGroups': predicateGroups
        };
        const dataSourceIds = getDataSourceIdsFromBinderTemplate(newBinderTemplate);
        dispatch(loadDataSourceItems(dataSourceIds));
        dispatch({
            type: LOAD_BINDER_DESCRIPTORS,
            payload: {
                institutionId,
                binderTemplate: newBinderTemplate,
                descriptors,
            }
        });
    };
}

export function getStudyEssentialsByStudyTypeId(institutionId, studyTypeId) {
    const query = nodeWithArgs('studyDescriptors', {
        institutionId,
        studyTypeId
    }, [
        'id',
        'studyTypeId',
        'name',
        'descriptorId',
        'institutionId',
        'dataSourceId',
        node('dataSources', [
            'value',
            'text',
            'dataType',
            'order'
        ]),
        'inputFieldType',
        'dataType',
        'type'
    ]);

    return queryGraphQL(toQuery(query));
}

export function getStudyDescriptors(institutionId, studyTypeId) {
    return async dispatch => {
        const studyEssentialResponse = await getStudyEssentialsByStudyTypeId(institutionId, studyTypeId);

        validateResolverResponse(studyEssentialResponse, dispatch, 'Failed to fetch study essentials');

        dispatch({
            type: LOAD_STUDY_DESCRIPTORS,
            payload: studyEssentialResponse.data.studyDescriptors
        });
    };
}

export function getIsMissigDetails(studyDetails, metadata) {
    const uniq = new Set(metadata.map(metadata => metadata.descriptorId));
    const setOfDescriptorIds = Array.from(uniq);
    let res = studyDetails.filter(
        studyDetail => !setOfDescriptorIds.some(
            descriptorId => studyDetail.descriptorId === descriptorId
        )
    );

    return res.length > 0;
}

export function checkForEmptyDetails() {
    return (dispatch, getState) => {
        const state = getState();
        const { study } = state;
        const missingDetails = lodash.get(state, 'study.isMissingDetail', undefined);
        const studyDetails = lodash.get(state, 'study.studyDetails', null);
        const metadata = lodash.get(state, 'study.metadata', null);

        if(!studyDetails || !metadata) return;

        const isMissingDetail = getIsMissigDetails(studyDetails, metadata);

        if(missingDetails !== isMissingDetail) {
            study['isMissingDetail'] = isMissingDetail;
            dispatch(updateStudy(study, study.currentStageId, [], [], false, false));
        }
    };
}

export function getStudyStages(institutionId) {
    return async dispatch => {
        const query = nodeWithArgs('getStudyStages', {
            institutionId
        }, [
            'id',
            'name',
            'description',
            'institutionId',
            'createdAt',
            'updatedAt'
        ]);

        const studyStagesResponse = await queryGraphQL(toQuery(query));
        validateResolverResponse(studyStagesResponse, dispatch, 'Failed to fetch study stages');
        dispatch({
            type: LOAD_STUDY_STAGES,
            payload: studyStagesResponse.data.getStudyStages
        });
    };
}


export function getStudyTeam(studyId,
    institutionId,
    limit,
    offset,
    filters,
    sortField,
    sortOrder,
    queryText) {
    return async dispatch => {
        const query = nodeWithArgs('getStudyTeam', {
            institutionId,
            studyId,
            limit,
            offset,
            filters,
            sortField,
            sortOrder,
            queryText
        }, ['totalCount',
            node('studyTeamMember', [
                'id',
                'userId',
                'studyId',
                'institutionId',
                'isClinicalStaff',
                'hasAdminRole',
                'nameOfResearcher',
                'studyRoleId',
                'studyUserType',
                node('studyRole', [
                    'id',
                    'name',
                ]),
                node('user', [
                    'displayName',
                    'sub',
                    'id',
                    'emailAddress',
                ]),
                node('delegation', [
                    'id',
                    node('rolesManagement', [
                        'id',
                        'name',
                        'institutionId'
                    ])
                ]),
            ])]);

        const studyTeamResponse = await queryGraphQL(toQuery(query));
        validateResolverResponse(studyTeamResponse, dispatch, 'Failed to fetch study admins');
        if (!limit && !offset)
            dispatch({
                type: LOAD_ALL_STUDY_TEAM,
                payload: studyTeamResponse.data.getStudyTeam
            });
        else
            dispatch({
                type: LOAD_STUDY_TEAM,
                payload: studyTeamResponse.data.getStudyTeam
            });

    };
}
export function getAllUsers() {
    return async (dispatch, getState) => {
        const state = getState();
        const study = lodash.get(state, 'study');
        const { institutionId } = study;
        const usersNode = nodeWithArgs('users', {
            institutionId,

        }, [
            'id',
            'firstName',
            'lastName',
            'displayName',
            'emailAddress',
            'sub',
            'createdAt',
            'isActive',
            nodeWithArgs('groups', {
                institutionId,
            }, [
                'id',
                'displayName',
                'description',
                'priority'
            ])]);
        const response = await queryGraphQL(toQuery(usersNode));

        dispatch({
            type: 'PART11_LOAD_USERS',
            payload: {
                users: response.data.users
            }
        });
    };
}

export function addStudyTeamMember(teamMember, studyUserType, ignoreStudyRefresh = false) {
    return async (dispatch, getState) => {
        const state = getState();
        const study = lodash.get(state, 'study');
        const { institutionId, binderId, id: studyId, createdBy: studyCreatorId } = study;
        const formattedTeamMember = teamMember.map((user) => (formatStudyTeamMember(study, user, studyUserType)));
        const studyMutation = nodeWithArgs('createStudyTeamMembers', {
            institutionId,
            studyId,
            studyTeamMembers: formattedTeamMember
        }, [
            'institutionId',
            'nameOfResearcher',
            'userId',
            'studyId',
            'studyRoleId',
            'isClinicalStaff',
            'studyUserType'
        ]);

        const addStudyTeamResponse = await mutateGraphQL(toMutation(studyMutation));

        validateResolverResponse(
            addStudyTeamResponse,
            dispatch,
            'An error occurred while adding team members'
        );

        if (addStudyTeamResponse.errors) {
            return;
        }

        const filteredTeamMembers = nonAdminTeamMembers(studyCreatorId, teamMember);
        if (!lodash.isEmpty(filteredTeamMembers)) {
            const studyTeamBinderAccessResponse = await setStudyTeamBinderAccess(
                getState(),
                filteredTeamMembers,
                'yes'
            );

            validateResolverResponse(
                studyTeamBinderAccessResponse,
                dispatch,
                'An error occurred while granting team members permission into binder');
        }

        const studyBinderResponse = await updateStudyBinder(binderId, studyId);
        validateResolverResponse(
            studyBinderResponse,
            dispatch,
            'An error occurred while updating study binder.'
        );

        if (studyBinderResponse.errors) {
            return;
        }

        // if (!lodash.isEmpty(teamMember.studyRoleId)) {
        //     const delegationResponse = await addDelegations(
        //         state, dispatch, teamMember, institutionId, binderId, studyId
        //     );

        //     validateResolverResponse(
        //         delegationResponse,
        //         dispatch,
        //         'An error occurred while adding delegations'
        //     );
        //     if (delegationResponse.errors) {
        //         return;
        //     }
        // }
        if (!ignoreStudyRefresh)
            dispatch(getStudyTeam(studyId, institutionId));

        const member = STUDY_TEAM_CAPITAL[studyUserType.toUpperCase()];

        dispatch(addApplicationMessage('success', `${member} successfully added.`));
    };
}

export function createTeamMember(teamMember, studyUserType, delegationData = null, includeDoA = true, ignoreStudyRefresh = false, showToaster = true) {
    return async (dispatch, getState) => {
        const state = getState();

        const study = lodash.get(state, 'study');
        const { institutionId, id: studyId, createdBy: studyCreatorId } = study;
        const studyRoleId = lodash.get(teamMember, 'studyRoleId', null);

        let delegation;
        if (studyRoleId && doaFeatureAccess(state, institutionId)) {
            const currentDOA = delegationsList(state);
            const activePI = getActivePI(state);

            delegation = delegationData ? delegationData : formatDelegation(
                {
                    id: teamMember.userId,
                    name: teamMember.displayName,
                    studyRoleId: teamMember.studyRoleId,
                    studyRoleName: teamMember.studyRoleName,
                },
                currentDOA,
                activePI,
            );
        }
        const studyMutation = nodeWithArgs('createStudyTeamMemberV2', {
            institutionId,
            studyId,
            studyCreatorId,
            userId: teamMember.userId,
            studyUserType,
            studyRoleId,
            nameOfResearcher: lodash.get(teamMember, 'nameOfResearcher', null),
            ...(includeDoA ? { delegation: delegation } : {}),
        }, [
            'id',
            'institutionId',
            'userId',
            'isClinicalStaff',
            // 'hasAdminRole',
            'studyUserType',
            'nameOfResearcher',
            'studyRoleId',
            node('studyRole', [
                'id',
                'name',
                'active',
            ]),
            'studyId',
            node('user', [
                'displayName',
                'sub',
                'id',
            ]),
            node('delegation', [
                'id',
                'binderId',
                'studyId',
                'taskIds',
                'startDate',
            ]),
        ]);

        const createTeamMemberResponse = await mutateGraphQL(toMutation(studyMutation));

        validateResolverResponse(
            createTeamMemberResponse,
            dispatch,
            'An error occurred while adding team members'
        );

        if (createTeamMemberResponse.errors) {
            return;
        }

        if (!!delegation && createTeamMemberResponse.data.createStudyTeamMemberV2.delegation) {

            const delegationResult = {
                ...createTeamMemberResponse.data.createStudyTeamMemberV2.delegation,
                user: createTeamMemberResponse.data.createStudyTeamMemberV2.user,
                rolesManagement: { ...createTeamMemberResponse.data.createStudyTeamMemberV2.studyRole },
                signatures: [],
            };

            dispatch({
                type: ADDED_STUDY_DELEGATIONS,
                payload: [delegationResult],
            });
            dispatch(loadStudySignaturesV2(delegationResult.binderId, studyId));
        }
        if (!ignoreStudyRefresh)
            dispatch(getStudyTeam(studyId, institutionId));


        const member = STUDY_TEAM_CAPITAL[studyUserType.toUpperCase()];



        if (!!delegation && !delegation.delegation.startDate && !ignoreStudyRefresh && showToaster) {
            dispatch(addApplicationMessage('success', `${member} successfully added.`));
            dispatch(addApplicationMessage('success',
                'An Active PI already is assigned to the study.  The new PI has been added without a start date.'
            ));
        } else {
            !!showToaster && dispatch(addApplicationMessage('success', `${member} successfully added.`));
        }
        if (!studyRoleId) {
            return createTeamMemberResponse.data.createStudyTeamMemberV2;
        }
    };
}

export function editStudyTeamMember(teamMember, studyUserType, studyRoleId, ignoreStudyRefresh = false, showToast = true) {
    return async (dispatch, getState) => {
        const state = getState();
        const { study } = state;
        const { institutionId, id: studyId, binderId } = study;
        const formattedTeamMember = formatStudyTeamMember(study, teamMember, studyUserType);

        const studyMutation = nodeWithArgs('editStudyTeamMember', {
            institutionId: institutionId,
            studyId,
            input: formattedTeamMember
        }, [
            'institutionId',
            'nameOfResearcher',
            'userId',
            'studyId',
            'studyRoleId',
            'isClinicalStaff',
            'studyUserType'
        ]);
        const editStudyTeamMemberResponse = await mutateGraphQL(toMutation(studyMutation));

        showToast && validateResponse(
            editStudyTeamMemberResponse,
            dispatch,
            'An error occurred while editing the team member'
        );
        if (editStudyTeamMemberResponse.errors) {
            return;
        }
        const studyBinderResponse = await updateStudyBinder(binderId, studyId);
        showToast && validateResolverResponse(
            studyBinderResponse,
            dispatch,
            'An error occurred while updating study binder.'
        );
        if (studyBinderResponse.errors) {
            return;
        }
        if (!ignoreStudyRefresh)
            dispatch(getStudyTeam(studyId, institutionId));

        let member = STUDY_TEAM_CAPITAL[studyUserType.toUpperCase()];

        if (teamMember.hasAdminRole && studyUserType === STUDY_USER_TYPE.TEAM) {
            member = 'Study User';
        }

        showToast && dispatch(addApplicationMessage('success', `${member} successfully updated.`));
    };
}

export function removeStudyTeamMember(teamMember, ignoreStudyRefresh = false, refereshStudyTeam, showToaster = true) {
    return async (dispatch, getState) => {
        const state = getState();
        const loggedSub = lodash.get(getState(), 'me.sub');
        const { study } = state;
        const { institutionId, id: studyId, binderId, createdBy: studyCreatorId } = study;

        const studyMutation = nodeWithArgs('removeStudyTeamMember', {
            institutionId: institutionId,
            studyId,
            studyTeamMember: {
                id: teamMember.id,
                studyId
            }
        });

        const removeStudyTeamMemberResponse = await mutateGraphQL(toMutation(studyMutation));

        validateResponse(
            removeStudyTeamMemberResponse,
            dispatch,
            'An error occurred while removing study team member from binder access.'
        );
        if (removeStudyTeamMemberResponse.errors) {
            return;
        }

        const filteredTeamMembers = nonAdminTeamMembers(studyCreatorId, [teamMember]);

        if (!lodash.isEmpty(filteredTeamMembers)) {
            const studyTeamBinderAccessResponse =
                await setStudyTeamBinderAccess(getState(), filteredTeamMembers, 'no');
            showToaster && (
                studyTeamBinderAccessResponse,
                dispatch,
                'An error occurred while granting team members permission into binder');
        }

        const studyBinderResponse = await updateStudyBinder(binderId, studyId);

        showToaster && validateResolverResponse(
            studyBinderResponse,
            dispatch,
            'An error occurred while updating study binder.'
        );
        if (studyBinderResponse.errors) {
            return;
        }

        dispatch(getStudy(studyId));

        let removeMessage = '';
        switch (teamMember.studyUserType) {
            case STUDY_USER_TYPE.TEAM:
                removeMessage = 'Study Team Member removed and their documents will remain in this study.';
                break;
            case STUDY_USER_TYPE.ADMIN:
                removeMessage = 'Study Admin successfully removed.';
                break;
            case STUDY_USER_TYPE.VIEWER:
                removeMessage = 'Study Viewer successfully removed.';
                break;
        }

        showToaster && dispatch(addApplicationMessage('success', removeMessage));
        // when user is removing himself
        if (loggedSub === teamMember.user.sub) {
            browserHistory.push('/');
        } else {
            if (!ignoreStudyRefresh)
                dispatch(getStudyTeam(studyId, institutionId));
            else if (refereshStudyTeam)
                refereshStudyTeam();
        }
    };
}


export async function addDelegations(state, dispatch, teamMember, institutionId, binderId, studyId) {
    const newDelegations = await createDelegationsV2({
        institutionId,
        binderId,
        studyId,
        users: [{
            id: teamMember.userId,
            name: teamMember.displayName,
            studyRoleId: teamMember.studyRoleId,
            studyRoleName: teamMember.studyRoleName,
        }],
        state: state,
        dispatch
    });
    if (!lodash.isEmpty(newDelegations)) {
        dispatch({
            type: ADDED_STUDY_DELEGATIONS,
            payload: newDelegations,
        });
        dispatch(loadStudySignaturesV2(binderId, studyId));
    }
    return newDelegations;
}

const setStudyTeamBinderAccess = (state, selectedTeamMembers, permit) => {
    const binderId = lodash.get(state, 'study.binderId');
    const institutionId = lodash.get(state, 'study.institutionId', userDefaultInstitutionIdSelector(state));
    const usersAtInstitution = lodash.get(state, 'studySettings.allTeamMembers');

    const studyTeamPermissions = selectedTeamMembers.reduce((acc, studyTeamMember) => {
        if (studyTeamMember.sub) {
            return lodash.set(acc, `${studyTeamMember.sub}.hasBinderAccess`, permit);
        }

        const matchingUser = lodash.find(usersAtInstitution, ['userId', studyTeamMember.userId]);

        // this only covers the edge case of an inactive user who is still on the study being removed
        if (lodash.isNil(matchingUser) && permit == 'no') {
            const studyTeam = lodash.get(state, 'studyTeam.studyTeamMember', []);
            const matchingInactiveUser = studyTeam.find(teamMember => teamMember.userId == studyTeamMember.userId);
            const sub = lodash.get(matchingInactiveUser, 'user.sub');

            return lodash.set(acc, `${sub}.hasBinderAccess`, permit);
        }

        return lodash.set(acc, `${matchingUser.sub}.hasBinderAccess`, permit);
    }, {});

    const studyTeamAccessMutation = nodeWithArgs('updateUserBinderPermissions', {
        userPermissions: {
            binderId,
            institutionId,
            permissions: JSON.stringify(studyTeamPermissions)
        }
    }, [
        'id',
        'sub',
        'displayName'
    ]);
    return mutateGraphQL(toMutation(studyTeamAccessMutation));
};

export function getTeamMembersByInstitution(institutionId) {
    return async (dispatch, getState) => {
        const storeState = getState();
        const { study: { id: studyId } } = storeState;
        const query = nodeWithArgs('studyUsers', {
            studyId,
            institutionId,
            sortField: 'displayName',
            filterField: true,
        }, [
            'id',
            'sub',
            'displayName',
            nodeWithArgs('groups', {
                institutionId
            }, [
                'displayName',
                'id'
            ]),
        ]);

        const getTeamMembersResponse = await queryGraphQL(toQuery(query));
        validateResolverResponse(getTeamMembersResponse, dispatch, 'Failed to fetch study team members');

        dispatch({
            type: LOAD_INSTITUTION_STUDY_TEAM_MEMBERS,
            payload: getTeamMembersResponse.data.studyUsers
        });
    };
}


export function unloadStudySettingsTeam() {
    return dispatch => {
        dispatch({
            type: UNLOAD_STUDY_SETTINGS_TEAM
        });
    };
}

export function updateStudyMetadata(institutionId, studyId, studyMetadataToAdd, studyMetadataToRemove) {
    if (lodash.isEmpty(studyMetadataToAdd) && lodash.isEmpty(studyMetadataToRemove)) {
        return;
    }
    const studyMetadataMutation = nodeWithArgs('updateStudyMetadata', {
        institutionId,
        studyId,
        studyMetadataToAdd,
        studyMetadataToRemove,
    }, ['id']);

    return mutateGraphQL(toMutation(studyMetadataMutation));
}

export function updateStudySetup(institutionId, studyId, binderId, studyMetadataToAdd, studyMetadataToRemove) {
    if (lodash.isEmpty(studyMetadataToAdd) && lodash.isEmpty(studyMetadataToRemove)) {
        return;
    }
    const studyMetadataMutation = nodeWithArgs('updateStudySetup', {
        institutionId,
        studyId,
        binderId,
        studyMetadataToAdd,
        studyMetadataToRemove,
    });

    return mutateGraphQL(toMutation(studyMetadataMutation));
}

export function updateStudyBinder(binderId, studyId, title, billable) {
    const studyBinderMutation = nodeWithArgs('updateStudyBinder', {
        binderId,
        studyId,
        title,
        billable
    });
    return mutateGraphQL(toMutation(studyBinderMutation));
}

export function updateStudyEssential(studyMetadataToAdd, studyMetadataToRemove) {
    return async (dispatch, getState) => {
        const storeState = getState();
        const { study: { institutionId, id: studyId } } = storeState;

        const updateStudyMetadataResponse = await updateStudyMetadata(institutionId, studyId, studyMetadataToAdd, studyMetadataToRemove);

        validateResolverResponse(updateStudyMetadataResponse, dispatch, 'Failed to update study metadata');
        dispatch(getStudy(studyId));
        dispatch(addApplicationMessage('success', 'Study Details successfully updated.'));
    };
}

export function updateStudyDetails(studyMetadataToAdd, studyMetadataToRemove) {
    return async (dispatch, getState) => {
        const storeState = getState();
        const { study: { institutionId, id: studyId, binderId } } = storeState;
        const updateStudySetupResponse = await updateStudySetup(institutionId, studyId, binderId, studyMetadataToAdd, studyMetadataToRemove);
        dispatch(checkForEmptyDetails());
        validateResolverResponse(updateStudySetupResponse, dispatch, 'Failed to update study metadata');
    };
}

export function processingStudySettings(status) {
    return {
        type: PROCESSING_STUDY_SETTINGS,
        payload: status
    };
}

function formatStudyTeamMember(study, teamMember, studyUserType) {
    return {
        institutionId: study.institutionId,
        isClinicalStaff: false,
        hasAdminRole: lodash.get(teamMember, 'hasAdminRole', false),
        studyId: study.id,
        studyRoleId: lodash.get(teamMember, 'studyRoleId', null),
        nameOfResearcher: lodash.get(teamMember, 'nameOfResearcher', null),
        userId: lodash.get(teamMember, 'userId', null),
        id: teamMember.id,
        studyUserType: studyUserType
    };
}


export function loadExternalUsersForStudy(binderId, institutionId, searchOptions) {
    return (dispatch, getState) => {
        const state = getState();
        const { study: { id: studyId } = {} } = state;
        const query = nodeWithArgs('studyExternalUsers', {
            institutionId,
            studyId,
            binderId,
            ...searchOptions,
        }, [
            'id',
            'sub',
            'institutionId',
            'emailAddress',
            'firstName',
            'lastName',
            'displayName',
            nodeWithArgs('binderExpiration', {
                binderId
            }, [
                'startDate',
                'endDate',
            ]),
        ]);
        dispatch(waitingForLoadExternalUsers(true));

        return queryGraphQL(toQuery(query)).then(results => {
            dispatch({
                type: LOAD_EXTERNAL_USERS_FOR_STUDY,
                payload: results.data.studyExternalUsers || [],
            });

            dispatch(waitingForLoadExternalUsers(false));
        });
    };
}

export function loadExternalUsersForStudyWithPagination(binderId, institutionId, searchOptions) {
    return (dispatch, getState) => {
        const state = getState();
        const { study: { id: studyId } = {} } = state;
        const query = nodeWithArgs('studyExternalUsersWithPagination', {
            institutionId,
            studyId,
            binderId,
            ...searchOptions,
        }, [
            'totalCount',
            node('externalUsers', [
                'id',
                'sub',
                'institutionId',
                'emailAddress',
                'firstName',
                'lastName',
                'displayName',
                'startDate',
                'endDate',
            ]),
        ]);
        dispatch(waitingForLoadExternalUsers(true));

        return queryGraphQL(toQuery(query)).then(results => {
            dispatch({
                type: LOAD_EXTERNAL_USERS_FOR_STUDY_WITH_PAGINATION,
                payload: results.data || [],
            });

            dispatch(waitingForLoadExternalUsers(false));
        });
    };
}



export function loadExternalUserForStudy(emailAddress) {
    return (dispatch, getState) => {
        const state = getState();
        const { study: { id: studyId, institutionId, binderId } = {} } = state;
        const query = nodeWithArgs('loadStudyUserByEmail', {
            emailAddress,
            institutionId,
            studyId,
        }, [
            'id',
            'sub',
            'emailAddress',
            'firstName',
            'lastName',
            'displayName',
            nodeWithArgs('binderExpiration', {
                binderId
            }, [
                'startDate',
                'endDate',
            ]),
        ]);

        dispatch(waitingForLoadExternalUser(true));

        return queryGraphQL(toQuery(query)).then(results => {
            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Error occurred while searching for user'));
            } else {

                let user = results.data.loadStudyUserByEmail;

                if (!user) {
                    user = {
                        emailAddress,
                    };
                }

                dispatch({
                    type: LOAD_EXTERNAL_USER_FOR_STUDY,
                    payload: user,
                });
            }

            dispatch(waitingForLoadExternalUser(false));
        });
    };
}

export function addOrEditStudyMonitor(user, binderId, institutionId, startDate, endDate, options) {
    const { edit } = options;
    return (dispatch, getState) => {
        const storeState = getState();
        const { study: { id: studyId } } = storeState;
        const query = nodeWithArgs('addStudyExternalUserToBinder', {
            binderId,
            studyId,
            institutionId,
            startDate,
            endDate,
            user,
        }, [
            'startDate',
            'endDate',
            node('user', [
                'id',
            ]),
        ]);

        return mutateGraphQL(toMutation(query)).then(results => {
            if (results && results.errors) {
                const serverMessage = results.errors[0] && results.errors[0].message;
                const defaultMessage = `Failed to ${edit ? 'edit' : 'create'} Study Monitor`;
                dispatch(addApplicationMessage('error', serverMessage || defaultMessage));

            } else {
                const resultData = results.data.addStudyExternalUserToBinder;
                dispatch({
                    type: ADD_EXTERNAL_USER,
                    payload: resultData,
                });

                const studyMonitor = resultData.user;

                if (studyMonitor) {
                    dispatch(addOrEditStudyMonitorForStudy(studyMonitor, startDate, endDate, options, user.firstName, user.lastName, user.displayName));
                }
            }
        });
    };
}

export function addOrEditStudyMonitorForStudy(studyMonitor, startDate, endDate, options, firstName, lastName, displayName) {
    const { notify, edit, paginationdata, filterRecord } = options;

    return async (dispatch, getState) => {
        const store = getState();
        const { study: { institutionId, id: studyId, binderId } } = store;

        const createStudyMonitorInput = {
            institutionId,
            monitorIsNewUser: !edit,
            shouldNotifyUser: notify,
            startDate,
            endDate,
            studyId,
            firstName,
            lastName,
            displayName,
            userId: studyMonitor.id,
        };

        const query = nodeWithArgs('createStudyMonitor', {
            institutionId: institutionId,
            studyId,
            input: createStudyMonitorInput,
        }, [
            'id',
            'institutionId',
            'studyId',
            'userId',
        ]);

        const response = await mutateGraphQL(toMutation(query));

        if (response && response.errors) {
            dispatch(addApplicationMessage('error', `Failed to ${edit ? 'edit' : 'create'} Study Monitor`));
        } else {
            dispatch({ type: CLEAR_EXTERNAL_USER });
            dispatch(loadBinderUsers(binderId));
            dispatch(loadExternalUsersForStudy(binderId, institutionId));
            if (paginationdata && filterRecord) {
                let sortField;
                let sortOrder = filterRecord.sort.value.toString() || '';
                if (filterRecord.sort.key === 'displayName') {
                    sortField = 'displayName';
                } else if (filterRecord.sort.key === 'accessStart') {
                    sortField = 'startDate';
                } else {
                    sortField = 'endDate';
                }
                dispatch(loadExternalUsersForStudyWithPagination(binderId, institutionId, { limit: paginationdata.limit, offset: paginationdata.offset, sortOrder: sortOrder === 'ASC' ? 'ASC' : 'DESC', sortField: sortField, queryText: filterRecord.search }));
            }
            dispatch(addApplicationMessage('success', `Study Monitor's access has been successfully ${edit ? 'updated' : 'created'}`));

            dispatch({
                type: CREATE_STUDY_MONITOR,
                payload: response.data.studyMonitor,
            });
        }
    };
}

export function removeStudyMonitor(user) {
    return async (dispatch, getState) => {
        const state = getState();
        const { study: { id: studyId } } = state;
        const binderId = lodash.get(state, 'study.binderId');
        const institutionId = userDefaultInstitutionIdSelector(state);
        const studyTeamBinderAccessResponse = await setStudyTeamBinderAccess(getState(), [user], 'no');

        const removeStudyMonitorMutation = nodeWithArgs('removeStudyMonitor', {
            institutionId,
            studyId,
            studyMonitor: {
                id: user.id,
                studyId
            },
        });


        validateResolverResponse(studyTeamBinderAccessResponse, dispatch, 'An error occurred while removing study monitor permission to binder');

        const removeStudyMonitorResponse = await mutateGraphQL(toMutation(removeStudyMonitorMutation));

        validateResolverResponse(removeStudyMonitorResponse, dispatch, 'An error occurred while removing monitor from study');
        if (!removeStudyMonitorResponse.errors) {
            dispatch(addApplicationMessage('success', 'Study monitor removed'));
        }
        dispatch(getStudy(studyId));
        dispatch(loadExternalUsersForStudy(binderId, institutionId));
        dispatch(loadExternalUsersForStudyWithPagination(binderId, institutionId));
    };
}

export function waitingForLoadExternalUser(status) {
    return {
        type: WAITING_FOR_LOAD_EXTERNAL_USER_FOR_STUDY,
        payload: status,
    };
}

export function waitingForLoadExternalUsers(status) {
    return {
        type: WAITING_FOR_LOAD_EXTERNAL_USERS_FOR_STUDY,
        payload: status,
    };
}

export function clearExternalUserForStudy() {
    return {
        type: CLEAR_EXTERNAL_USER_FOR_STUDY,
    };
}

export function saveStudyEssentials(binder) {
    return (dispatch, getState) => {
        const state = getState();
        const { study: { id: studyId } } = state;
        const renewalDate = lodash.isNil(binder.renewalDate) ? null : binder.renewalDate.toString();
        const binderValues = lodash.pick(binder, [
            'title',
            'description',
            'trialType',
            'primaryInvestigator',
            'irbNumber',
            'contact',
            'trialId',
            'departmentSiteDg',
            'ctGovNumber',
            'sponsorCro',
            'status',
        ]);

        const partnerMetadata = JSON.stringify(lodash.get(binder, ['partnerMetadata']));

        const mutation = toMutation([
            nodeWithArgs('updateBinderDetails', {
                id: binder.id,
                binder: { ...binderValues, renewalDate, partnerMetadata },
            }, [
                node('binder', [
                    'title',
                    'description',
                    'trialType',
                    'primaryInvestigator',
                    'irbNumber',
                    'contact',
                    'trialId',
                    'departmentSiteDg',
                    'ctGovNumber',
                    'sponsorCro',
                    'renewalDate',
                    'status',
                    node('partnerMetadata', [
                        'connexusStudyId',
                        'connexusSiteId',
                        'velosStudyId',
                    ]),
                ])
            ])
        ]);

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages(
                    'error',
                    'Failed to save study details'));
            } else {
                dispatch(getStudy(studyId));
                dispatch(addApplicationMessage(
                    'success',
                    'Study details has been successfully updated'));
            }
        });
    };
}

export function updateStudyEssentialsV4(binder) {
    return (dispatch, getState) => {
        const state = getState();
        const { study: { id: studyId } } = state;
        const renewalDate = lodash.isNil(binder.renewalDate) ? null : binder.renewalDate.toString();
        const binderValues = lodash.pick(binder, [
            'sponsor',
            'trialId',
            'contact',
            'cro',
            'ctGovNumber',
            'department',
            'diseaseGroup',
            'irbIecNumber',
            'primaryInvestigator',
            'renewalDate',
            'site',
            'siteNumber'
        ]);

        const partnerMetadata = JSON.stringify(lodash.get(binder, ['partnerMetadata']));

        const mutation = toMutation([
            nodeWithArgs('updateBinderDetails', {
                id: binder.id,
                binder: { ...binderValues, renewalDate, partnerMetadata },
            }, [
                node('binder', [
                    'title',
                    'description',
                    'trialType',
                    'primaryInvestigator',
                    'irbNumber',
                    'contact',
                    'trialId',
                    'departmentSiteDg',
                    'ctGovNumber',
                    'sponsorCro',
                    'renewalDate',
                    'status',
                    node('partnerMetadata', [
                        'connexusStudyId',
                        'connexusSiteId',
                        'velosStudyId',
                    ]),
                ])
            ])
        ]);

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages(
                    'error',
                    'Failed to save study details'));
            } else {
                dispatch(getStudy(studyId));
                dispatch(addApplicationMessage(
                    'success',
                    'Study details has been successfully updated'));
            }
        });
    };
}

export const loadStudyNotifiedUsers = studyId => async (dispatch) => {
    dispatch(setStudyNotifiedUsersLoadedStatusAction(false));
    const loadStudyNotifiedUsersQuery = nodeWithArgs('loadStudyNotifiedUsers', {
        studyId
    }, [
        'id',
        'sub',
        'emailAddress',
        'displayName'
    ]);

    const {
        errors,
        data: {
            loadStudyNotifiedUsers: studyNotifiedUsers
        }
    } = await queryGraphQL(toQuery(loadStudyNotifiedUsersQuery));
    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to load study notified users.'));
        return;
    }
    dispatch(loadStudyNotifiedUsersSuccessAction(studyNotifiedUsers));
    dispatch(setStudyNotifiedUsersLoadedStatusAction(true));
};

export const saveStudyNotifiedUsers = ({
    studyId,
    addedUserIds,
    removedUserIds
}) => async (dispatch) => {
    const saveStudyNotifiedUsersQuery = nodeWithArgs('saveStudyNotifiedUsers', {
        studyId,
        addedUserIds,
        removedUserIds
    }, [
        'id',
        'sub',
        'emailAddress',
        'displayName'
    ]);

    const {
        errors,
        data: {
            saveStudyNotifiedUsers: studyNotifiedUsers
        }
    } = await mutateGraphQL(toMutation(saveStudyNotifiedUsersQuery));
    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to save study notified users.'));
        return;
    }
    dispatch(saveStudyNotifiedUsersSuccessAction(studyNotifiedUsers));
    dispatch(addApplicationMessage('success', 'Updated list of notified users successfully.'));
};

export const clearStudyNotifiedUsers = () => dispatch => {
    dispatch(clearStudyNotifiedUsersAction());
    dispatch(setStudyNotifiedUsersLoadedStatusAction(false));
};

export const setStudyNotifiedUsersLoadedStatusAction = status => ({
    type: SET_STUDY_NOTIFIED_USERS_LOADED_STATUS,
    payload: status
});

export const loadStudyNotifiedUsersSuccessAction = users => ({
    type: LOAD_STUDY_NOTIFIED_USERS_SUCCESS,
    payload: users
});

export const saveStudyNotifiedUsersSuccessAction = users => ({
    type: SAVE_STUDY_NOTIFIED_USERS_SUCCESS,
    payload: users
});

export const clearStudyNotifiedUsersAction = () => ({
    type: CLEAR_STUDY_NOTIFIED_USERS
});


const formatDelegation = (user, currentDOA, activePI) => {

    let signatures = [];

    if (user.studyRoleName === PRINCIPAL_INVESTIGATOR_ROLE) {

        const delegationsOverlappingWithTenure = delegationsUnderPITenurePredicate(currentDOA, [new Date(), null,]);

        const signaturesForNewPI = lodash.map(delegationsOverlappingWithTenure, d => ({
            delegationId: d.id,
            user: {
                id: user.id,
                name: user.name,
            },
            signatureType: 'PI_START',
        }));

        signatures.push(...signaturesForNewPI);
    }

    const startDateOrNullIfActivePI = () => {
        const startDate = convertToISOString(parseDateFormat(moment().format('LL')));

        if (user.studyRoleName !== PRINCIPAL_INVESTIGATOR_ROLE) {
            return startDate;
        }

        if (!activePI) {
            return startDate;
        }

        return null;
    };

    const delegation = {
        delegation: {
            // id: uuid.v4(),
            startDate: startDateOrNullIfActivePI(),
            user: {
                id: user.id,
                name: user.name
            },
            roleManagementId: user.studyRoleId,
            auditOrigin: 'Study Settings',
            auditActivity: 'A corresponding delegation is created when a Study Team Member is added in Study Settings.'

        },
        signatureExpectations: signatures,
        defaultSignatureExpectations: signatures,
    };

    return delegation;
};
